import React from 'react';
import type { GridBlock } from './plantafelTypes';
import type { PlantafelViewMode } from './plantafelTypes';
import { usePlantafelDisplay } from './PlantafelTimelineContext';
import {
  blockSpanToPct,
  clientXToPct,
  pctSpanToBlockHours,
  spanToIsoRange,
} from './plantafelBlockTime';

type DragState = {
  edge: 'start' | 'end';
  rect: DOMRect;
  anchorLeftPct: number;
  anchorRightPct: number;
  moved: boolean;
};

export function usePlantafelBlockResize({
  block,
  viewMode,
  viewportStart,
  activityId,
  timelineRef,
  onTimeChange,
  enabled,
  timeZone,
}: {
  block: GridBlock;
  viewMode: PlantafelViewMode;
  viewportStart: Date;
  activityId: string | undefined;
  timelineRef: React.RefObject<HTMLDivElement | null> | null;
  onTimeChange?: (activityId: string, due_at: string, end_at: string) => void;
  enabled: boolean;
  timeZone: string;
}) {
  const { visibleWeekdays, dayHours } = usePlantafelDisplay();
  const [preview, setPreview] = React.useState<Pick<
    GridBlock,
    'dayStart' | 'dayEnd' | 'hourStart' | 'hourEnd'
  > | null>(null);
  const dragRef = React.useRef<DragState | null>(null);
  const suppressClickRef = React.useRef(false);

  const displayBlock = preview
    ? (() => {
        const iso = spanToIsoRange(viewportStart, preview, timeZone);
        return {
          ...block,
          ...preview,
          scheduleDueAt: iso.due_at,
          scheduleEndAt: iso.end_at,
        };
      })()
    : block;

  const commitDrag = (finalSpan: Pick<GridBlock, 'dayStart' | 'dayEnd' | 'hourStart' | 'hourEnd'>) => {
    const drag = dragRef.current;
    dragRef.current = null;
    setPreview(null);
    if (!drag?.moved || !activityId || !onTimeChange) return;
    suppressClickRef.current = true;
    const iso = spanToIsoRange(viewportStart, finalSpan, timeZone);
    onTimeChange(activityId, iso.due_at, iso.end_at);
  };

  const onResizePointerDown = (edge: 'start' | 'end', e: React.PointerEvent) => {
    if (!enabled || !activityId) return;
    const el = timelineRef?.current;
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = el.getBoundingClientRect();
    const { leftPct, widthPct } = blockSpanToPct(
      block,
      viewMode as 'tag' | 'woche',
      visibleWeekdays,
      dayHours,
    );
    dragRef.current = {
      edge,
      rect,
      anchorLeftPct: leftPct,
      anchorRightPct: leftPct + widthPct,
      moved: false,
    };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onResizePointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const xPct = clientXToPct(e.clientX, drag.rect);
    let leftPct = drag.anchorLeftPct;
    let rightPct = drag.anchorRightPct;
    if (drag.edge === 'start') {
      leftPct = Math.min(xPct, rightPct - 0.5);
    } else {
      rightPct = Math.max(xPct, leftPct + 0.5);
    }
    const widthPct = rightPct - leftPct;
    const span = pctSpanToBlockHours(
      leftPct,
      widthPct,
      viewMode as 'tag' | 'woche',
      visibleWeekdays,
      dayHours,
    );
    if (
      span.dayStart !== block.dayStart ||
      span.dayEnd !== block.dayEnd ||
      span.hourStart !== block.hourStart ||
      span.hourEnd !== block.hourEnd
    ) {
      drag.moved = true;
    }
    setPreview(span);
  };

  const onResizePointerUp = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    e.stopPropagation();
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    const xPct = clientXToPct(e.clientX, drag.rect);
    let leftPct = drag.anchorLeftPct;
    let rightPct = drag.anchorRightPct;
    if (drag.edge === 'start') {
      leftPct = Math.min(xPct, rightPct - 0.5);
    } else {
      rightPct = Math.max(xPct, leftPct + 0.5);
    }
    const span = pctSpanToBlockHours(
      leftPct,
      rightPct - leftPct,
      viewMode as 'tag' | 'woche',
      visibleWeekdays,
      dayHours,
    );
    commitDrag(span);
  };

  const consumeSuppressClick = () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return true;
    }
    return false;
  };

  return {
    displayBlock,
    onResizePointerDown,
    onResizePointerMove,
    onResizePointerUp,
    isResizing: preview != null,
    consumeSuppressClick,
  };
}
